import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, User, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthStore";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Already authenticated → bounce to where they came from (or home).
  if (user) {
    const dest = location.state?.from?.pathname || "/";
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid username or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-lines flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md" data-testid="login-page">
        <div className="panel relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(120% 120% at 80% 0%, rgba(51,183,109,0.10), transparent 60%)" }} />
          <div className="relative p-7">
            {/* brand */}
            <div className="flex items-center gap-2.5 mb-6">
              <img src="/brand/ergoniq-logo.svg" alt="Ergoniq" className="h-14 w-auto"
                style={{ filter: "brightness(0) invert(1)" }} />
              <div className="h-10 w-px bg-border" />
              <div className="leading-none">
                <div className="font-display font-extrabold text-base tracking-tight text-ink">OIM-AKRY</div>
                <div className="text-[0.58rem] font-mono text-ink-soft tracking-wider">COAL→PRODUCT INTELLIGENCE</div>
              </div>
            </div>

            <div className="eyebrow mb-1" style={{ color: "#33b76d" }}>Secure Access</div>
            <h1 className="font-display font-extrabold text-2xl uppercase tracking-tight text-ink mb-5">Sign in</h1>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <div className="eyebrow mb-1">Username</div>
                <div className="relative">
                  <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                  <input data-testid={LOGIN.usernameInput} autoFocus value={username}
                    onChange={(e) => setUsername(e.target.value)} autoComplete="username"
                    className="w-full panel-2 pl-9 pr-3 py-2.5 text-ink font-mono text-sm focus:outline-none focus:border-brand-green"
                    placeholder="admin" />
                </div>
              </div>

              <div>
                <div className="eyebrow mb-1">Password</div>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                  <input data-testid={LOGIN.passwordInput} type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                    className="w-full panel-2 pl-9 pr-3 py-2.5 text-ink font-mono text-sm focus:outline-none focus:border-brand-green"
                    placeholder="••••••••" />
                </div>
              </div>

              {error && (
                <div data-testid={LOGIN.errorMessage}
                  className="text-[0.72rem] font-mono px-3 py-2 border border-alert/40 text-alert"
                  style={{ background: "rgba(229,72,77,0.10)" }}>
                  {error}
                </div>
              )}

              <button data-testid={LOGIN.submitButton} type="submit" disabled={loading || !username || !password}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-green text-black font-display font-bold uppercase tracking-wider text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
